import {
  deleteAvsKvps,
  editAvsKvps,
  addAvsKvps,
} from "@/app/lib/actions/avs-kvps";
import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";
import { useAvsKvpsQuery, invalidateAvsKvpsQuery } from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";

import { addAvsKvpsSchema, editAvsKvpsSchema } from "@/lib/schemas/avs-kvps";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { toast } from "sonner";

type AvsKvps = {
  id: number;
  label: string;
  kvps: string;
  isActive: boolean;
};

function AvsKvpsItemsListCard() {
  const queryClient = useQueryClient();

  const avsKvpsQuery = useAvsKvpsQuery({ sort: { label: "asc" } });
  const handleCreate = async (avsKvps: z.infer<typeof addAvsKvpsSchema>) => {
    const response = await addAvsKvps(avsKvps);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Nouveau KVPS AVS créé.");
      invalidateAvsKvpsQuery(queryClient);
    }
  };

  const handleUpdate = async (avsKvps: z.infer<typeof editAvsKvpsSchema>) => {
    const response = await editAvsKvps(avsKvps);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("KVPS AVS modifié.");
      invalidateAvsKvpsQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteAvsKvps(id);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("KVPS AVS supprimé.");
      invalidateAvsKvpsQuery(queryClient);
    }
  };

  if (avsKvpsQuery.isPending) return <ItemsListCardSkeleton />;
  const avsKvps = avsKvpsQuery.data?.avsKvps ?? [];

  return (
    <ItemsListCard<AvsKvps>
      title="KVPS AVS"
      items={avsKvps}
      createSchema={addAvsKvpsSchema}
      editSchema={editAvsKvpsSchema}
      defaultCreateValues={{ label: "", kvps: "", isActive: true }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <>
          <ItemContent>
            <ItemTitle>{item.label}</ItemTitle>
          </ItemContent>
        </>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <form.Field name="label">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Libellé</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={200}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="kvps">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Identifiant KVPS</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={9}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>
                <Switch
                  id="switch"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { AvsKvpsItemsListCard };
